import React, { useState } from "react";
import { useFormContext } from "react-hook-form";
import { Upload, File, X, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import * as Sentry from "@sentry/react";

interface DocumentUploadProps {
  tier: string;
}

interface UploadedFile {
  file: File;
  id: string;
  uploading: boolean;
  uploaded: boolean;
  storagePath?: string;
}

const DocumentUpload = ({ tier }: DocumentUploadProps) => {
  const {
    setValue,
    formState: { errors },
  } = useFormContext();
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [dragActive, setDragActive] = useState(false);

  const hasError = !!errors.uploadedDocuments;

  const getInstructionText = () => {
    if (tier === "Group 1" || tier === "Eligibility Criteria 1") {
      return "Upload your Ministry of Education award certificate to confirm your GPA. This is required for high school graduates applying for Group 1 membership.";
    } else {
      return "Upload your confirmation of enrollment or you degree certificate from a Top 100 university to confirm your membership eligibility.";
    }
  };

  const validateFile = (file: File): string | null => {
    if (file.type !== "application/pdf") {
      return "Only PDF files are allowed";
    }
    if (file.size > 10 * 1024 * 1024) {
      // 10MB limit
      return "File size must be less than 10MB";
    }
    return null;
  };

  const uploadFile = async (file: File): Promise<string | null> => {
    try {
      const fileExt = "pdf";
      const fileName = `${Date.now()}-${Math.random()
        .toString(36)
        .substring(2)}.${fileExt}`;
      const filePath = `certificates/${fileName}`;

      Sentry.addBreadcrumb({
        message: "Starting file upload to Supabase storage",
        category: "storage",
        level: "info",
        data: {
          file_name: file.name,
          file_size: file.size,
          storage_path: filePath,
        },
      });

      const { data, error } = await supabase.storage
        .from("application-attachments")
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (error) {
        console.error("Upload error:", error);
        Sentry.captureException(new Error("File upload failed"), {
          tags: {
            component: "DocumentUpload",
            operation: "file_upload",
          },
          contexts: {
            supabase_error: {
              message: error.message,
            },
          },
          extra: {
            file_name: file.name,
            file_size: file.size,
            storage_path: filePath,
          },
        });
        return null;
      }

      return data.path;
    } catch (error) {
      console.error("Upload exception:", error);
      Sentry.captureException(error, {
        tags: {
          component: "DocumentUpload",
          operation: "file_upload_exception",
        },
        extra: {
          file_name: file.name,
          file_size: file.size,
        },
      });
      return null;
    }
  };

  const handleFiles = async (files: FileList) => {
    const fileArray = Array.from(files);

    for (const file of fileArray) {
      const validationError = validateFile(file);
      if (validationError) {
        toast.error(`${file.name}: ${validationError}`);
        continue;
      }

      const fileId = Math.random().toString(36).substring(2);
      const newFile: UploadedFile = {
        file,
        id: fileId,
        uploading: true,
        uploaded: false,
      };

      setUploadedFiles((prev) => [...prev, newFile]);

      // Upload file
      const storagePath = await uploadFile(file);

      setUploadedFiles((prev) =>
        prev.map((f) =>
          f.id === fileId
            ? {
                ...f,
                uploading: false,
                uploaded: !!storagePath,
                storagePath: storagePath || undefined,
              }
            : f
        )
      );

      if (storagePath) {
        toast.success(`${file.name} uploaded successfully`);
        Sentry.addBreadcrumb({
          message: "File uploaded successfully",
          category: "storage",
          level: "info",
          data: {
            file_name: file.name,
            storage_path: storagePath,
          },
        });
      } else {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const removeFile = (fileId: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  // Update form with uploaded files data
  React.useEffect(() => {
    const uploadedData = uploadedFiles
      .filter((f) => f.uploaded && f.storagePath)
      .map((f) => ({
        file_name: f.file.name,
        file_type: f.file.type,
        storage_path: f.storagePath!,
      }));
    setValue("uploadedDocuments", uploadedData);
  }, [uploadedFiles, setValue]);

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2 font-verona">
          Document Upload *
        </label>

        <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-start space-x-2">
            <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-800 font-verona">
              {getInstructionText()}
            </p>
          </div>
          <p className="text-xs text-blue-700 mt-2 font-verona">
            You can upload multiple documents, but at least one must be the
            required certificate mentioned above.
          </p>
        </div>
      </div>

      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          hasError
            ? "border-red-500 bg-red-50"
            : dragActive
            ? "border-primary bg-primary/5"
            : "border-neutral-light hover:border-primary hover:bg-primary/5"
        }`}
        onDragEnter={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setDragActive(false);
        }}
        onDragOver={(e) => {
          e.preventDefault();
        }}
        onDrop={handleDrop}
      >
        <Upload className="h-8 w-8 text-gray-400 mx-auto mb-3" />
        <p className="text-sm text-gray-600 font-verona mb-2">
          Drag and drop PDF files here, or{" "}
          <label className="text-primary hover:text-primary/80 cursor-pointer font-medium">
            browse files
            <input
              type="file"
              accept=".pdf"
              multiple
              className="hidden"
              onChange={handleFileSelect}
            />
          </label>
        </p>
        <p className="text-xs text-gray-500 font-verona">
          PDF files only, max 10MB each
        </p>
      </div>

      {uploadedFiles.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700 font-verona">
            Uploaded Files:
          </h4>
          {uploadedFiles.map((file) => (
            <div
              key={file.id}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
            >
              <div className="flex items-center space-x-3">
                <File className="h-4 w-4 text-gray-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900 font-verona">
                    {file.file.name}
                  </p>
                  <p className="text-xs text-gray-500 font-verona">
                    {(file.file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {file.uploading && (
                  <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                )}
                {file.uploaded && (
                  <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                    <svg
                      className="w-2 h-2 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
                {!file.uploading && (
                  <button
                    type="button"
                    onClick={() => removeFile(file.id)}
                    className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {hasError && (
        <p className="text-red-500 text-sm mt-2 font-verona">
          {errors.uploadedDocuments?.message as string}
        </p>
      )}
    </div>
  );
};

export default DocumentUpload;
